/**
 * The reader-accessible master data reads every screen with a controlled-value
 * control depends on.
 *
 * ## Why not the administration route
 *
 * `GET /api/admin/master-data/:set` is gated on `masterdata:admin`, a
 * System_Administrator-only capability. A planner filtering the calendar, filling
 * in the intake form or scoping a Manpower view holds `initiative:read` and not
 * that, so reading the administration route is refused at the capability layer
 * before the handler runs - and the symptom is a silently empty dropdown, not an
 * error anybody sees. `GET /api/master-data/:set/selectable` is the read gated on
 * `initiative:read`, and it returns the narrower population R16.5 permits as *new*
 * selections: active and effective as of the system date.
 *
 * ## `includeIds`, and why a filter needs it
 *
 * R11.2 requires the calendar's master-data filters to offer "the active records
 * of each dimension **plus any inactive record referenced by at least one
 * Initiative of the selected planning year**", and R11.15 requires a Saved_View's
 * stored value to keep being applied and named even after that value is
 * deactivated. Both need records the active-and-effective population excludes,
 * which is what `includeIds` asks for: the server returns those ids too, flagged
 * inactive. Passing the currently selected ids therefore keeps a deactivated value
 * both selectable and labelled instead of collapsing it to a bare identifier.
 *
 * This module exists because three features previously each grew their own copy of
 * this fetch (the intake form's `intake-options-data.ts`, the Manpower screens'
 * `manpower-reference-data.ts`, and the calendar's own identifier inputs); the
 * option *shapes* they need differ, but the request and its schema do not.
 *
 * ## Why every consumer must cache {@link SelectableRecord} and nothing narrower
 *
 * `queryKeys.selectableMasterData(set, includeIds)` is one cache entry, and a
 * TanStack Query entry holds whatever its `queryFn` returned - there is no
 * per-consumer copy. So a second `queryFn` on the same key does not merely
 * duplicate a request: it *overwrites the shape*, and with a five-minute
 * `staleTime` the other consumers then read that shape without refetching.
 *
 * That is exactly what went wrong on the Initiative list. It cached
 * `{ id, label, isInactive, colorHex }` under
 * `['reference', 'master-data', 'platform', '']`, while this module caches
 * `{ id, name, code, isActive, colour, … }` under the same key, and the intake
 * form cached a third, narrower `{ id, name, isActive }`. Whichever screen the
 * user opened first won. Arriving at the Initiative list *from the Calendar_View*
 * - whose filter bar populates this key through this module - handed the list
 * records with no `label` and no `colorHex`, so every Platform cell rendered its
 * raw identifier with no colour. A full page refresh "fixed" it only because the
 * list then fetched the key first and wrote its own shape back.
 *
 * The rule that prevents the whole class of bug: **one `queryFn` per key, one
 * cached shape, projections at the edge.** A consumer that needs a different
 * shape uses `select` (per-observer, never written to the cache) or a `useMemo`
 * over `data`. If a projection needs a field this record lacks, add it here.
 */
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { masterDataListResponseSchema, type MasterDataSet } from '@complan/contracts';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';

/** Reference data changes on an administrator's timescale, not a reader's. */
export const REFERENCE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * One selectable master data record.
 *
 * **This is the one shape cached under
 * {@link queryKeys.selectableMasterData}.** Every consumer of that key must read
 * and write exactly this, projecting to whatever its own controls need *outside*
 * the cache (React Query's `select`, or a `useMemo` over `data`). Three features
 * previously cached three different narrower shapes under the same key - see this
 * module's note below - and whichever fetched last silently decided what fields
 * the others could see.
 *
 * So the fields here are the union of what any consumer needs, not the minimum:
 * a field omitted because "this screen does not use it" is a field the next
 * screen finds missing.
 */
export interface SelectableRecord {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  /** `false` exactly when the record came back only because `includeIds` named it (R11.2, R11.15). */
  readonly isActive: boolean;
  /** Platform only: the configured bar/tag colour, `#rrggbb` or `null` (R16.1). */
  readonly colour: string | null;
  /** POS_Segment only: the OneData-sourced impacted POS count (R4.2). */
  readonly posCount: number | null;
  /** POS_Segment only: when that count was last sourced (R9.4). */
  readonly sourceTimestamp: string | null;
}

function withIncludeIds(path: string, includeIds: readonly string[]): string {
  if (includeIds.length === 0) {
    return path;
  }
  const query = new URLSearchParams({ includeIds: includeIds.join(',') });
  return `${path}?${query.toString()}`;
}

/** Fetches one set's selectable records. */
export async function fetchSelectableMasterData(
  set: MasterDataSet,
  includeIds: readonly string[] = [],
): Promise<readonly SelectableRecord[]> {
  const body = await apiFetch<unknown>(
    withIncludeIds(`/api/master-data/${set}/selectable`, includeIds),
  );
  const parsed = masterDataListResponseSchema.parse(body);
  return parsed.items.map((item) => ({
    id: item.id,
    name: item.name,
    code: item.code,
    isActive: item.isActive,
    colour: item.colour,
    posCount: item.posCount,
    sourceTimestamp: item.sourceTimestamp,
  }));
}

/** One master data set's selectable records. */
export function useSelectableMasterData(
  set: MasterDataSet,
  options?: { readonly enabled?: boolean; readonly includeIds?: readonly string[] },
): UseQueryResult<readonly SelectableRecord[], unknown> {
  const includeIds = options?.includeIds ?? [];
  return useQuery({
    queryKey: queryKeys.selectableMasterData(set, includeIds),
    queryFn: () => fetchSelectableMasterData(set, includeIds),
    staleTime: REFERENCE_STALE_TIME_MS,
    enabled: options?.enabled ?? true,
  });
}

/** What {@link useSelectableMasterDataSets} reports per requested set. */
export interface SelectableSetsResult {
  /** The records of each requested set, keyed by set name. Empty until resolved. */
  readonly bySet: ReadonlyMap<MasterDataSet, readonly SelectableRecord[]>;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Several sets in one hook, requested concurrently.
 *
 * `useQueries` rather than one `useQuery` per set so the sets are in flight
 * together: a filter bar needs seven of them, and serialising would make its first
 * paint wait seven round trips deep instead of one.
 *
 * `includeIdsBySet` is consulted per set, so each dimension asks only for the ids
 * it actually holds.
 */
export function useSelectableMasterDataSets(
  sets: readonly MasterDataSet[],
  options?: {
    readonly enabled?: boolean;
    readonly includeIdsBySet?: Partial<Record<MasterDataSet, readonly string[]>>;
  },
): SelectableSetsResult {
  const enabled = options?.enabled ?? true;
  const results = useQueries({
    queries: sets.map((set) => {
      const includeIds = options?.includeIdsBySet?.[set] ?? [];
      return {
        queryKey: queryKeys.selectableMasterData(set, includeIds),
        queryFn: () => fetchSelectableMasterData(set, includeIds),
        staleTime: REFERENCE_STALE_TIME_MS,
        enabled,
      };
    }),
  });

  const bySet = new Map<MasterDataSet, readonly SelectableRecord[]>();
  sets.forEach((set, index) => {
    bySet.set(set, results[index]?.data ?? []);
  });

  return {
    bySet,
    isLoading: results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
  };
}

/** An id → name lookup over one or more record lists. */
export function toNameLookup(
  ...lists: readonly (readonly SelectableRecord[] | undefined)[]
): ReadonlyMap<string, string> {
  const lookup = new Map<string, string>();
  for (const list of lists) {
    for (const record of list ?? []) {
      lookup.set(record.id, record.name);
    }
  }
  return lookup;
}
